import React from 'react';
import PropTypes from 'prop-types';
import { useClickOutside } from '../../hooks';
import AdvancedSearch from '.';
import SearchByKeyword from './SearchByKeyword';
import SearchChipsCarousel from '../SearchBar/SearchChipsCarousel';
import Icon from '../Icons/Icon';
import HeaderIconTooltip from '../HeaderIconTooltip/HeaderIconTooltip';
import {
  paramLabels, excludedKeys,
} from '../../utils/advancedSearch';

const DEFAULT_SITES_ATTRIBUTES = {};

const AdvancedSearchWrapper = ({
  setIsSearchExpanded, searchTitle = 'search sites', sitesAttributes = DEFAULT_SITES_ATTRIBUTES, params, setParams, search, searchParams, setSearchParams, updateSearchParamsAndUrl, isSearchExpanded, isRoutePage = false, hideOffRouteSites = () => {}, zoomToRoute = () => {}, isHideMarkers = false,
}) => {
  const modalRef = useClickOutside(isSearchExpanded, () => setIsSearchExpanded(false));

  const isArrivalAndDepartureChosen = !!searchParams.arrival && !!searchParams.departure;
  const chips = Object.entries(searchParams)
    .filter(([key, value]) => value && value !== '' && !excludedKeys.includes(key))
    .map(([key, value]) => {
      const label = paramLabels[key];
      const displayValue = typeof value === 'object' && value.label ? value.label : value;
      // special modifications for arrival and departure chips label only if one of them is chosen
      if (key === 'arrival' && !isArrivalAndDepartureChosen) {
        return {
          key,
          value: `arrival on: ${displayValue}`,
        };
      }
      if (key === 'departure' && !isArrivalAndDepartureChosen) {
        return {
          key,
          value: `departure on: ${displayValue}`,
        };
      }
      // default case
      return {
        key,
        value: `${label && `${label}:`} ${displayValue}`,
      };
    });

  const onClickCloseChip = (e, key) => {
    e.preventDefault();
    e.stopPropagation();
    setSearchParams(prev => {
      const newParams = { ...prev };
      delete newParams[key];
      return newParams;
    });
    const newSearchParams = { ...searchParams };
    delete newSearchParams[key];
    updateSearchParamsAndUrl(newSearchParams);
  };
  const hideShowRouteButton = isHideMarkers ? 'show_off_route' : 'hide_off_route';
  const hideShowTooltipText = isHideMarkers ? 'Show off-route sites' : 'Hide off-route sites';

  const showZoomAndOffSitesButtons = (
    <div className="flex w-23.5 border-l border-vamos-blue-gray border-1px h-full justify-center items-center gap-4 flex-shrink-0">

      <HeaderIconTooltip
        spanTextStart="Zoom to route"
        place="bottom-end"
        iconName="zoom_to_route"
        tooltipId="zoom-to-route-tooltip"
        paddingBottom="0"
        gap="gap-0"
        margins=""
        border="border-0"
        onClick={zoomToRoute}
      />
      <HeaderIconTooltip
        spanTextStart={hideShowTooltipText}
        place="bottom-end"
        iconName={hideShowRouteButton}
        tooltipId="hide-off-route-tooltip"
        paddingBottom="0"
        gap="gap-0"
        margins=""
        border="border-0"
        onClick={hideOffRouteSites}
        classForWrappingIconDiv="w-7.5"
        iconClass="mx-auto"
      />
    </div>
  );
  return (
    <>
      {(
        isSearchExpanded
          ? (

            <>
              <form
                ref={modalRef}
                className={`mx-auto border border-vamos-blue-gray rounded-md bg-vamos-true-white shadow-vamos-drop-shadow z-10 px-4 pt-2 pb-0 ${isRoutePage ? 'fixed w-max lg:min-w-209 right-8 top-16' : 'absolute w-1/2 mt-8 right-9 top-8'}`}
                onSubmit={(e) => { e.preventDefault(); search(); setIsSearchExpanded(false); }}
              >
                <SearchByKeyword shouldExpand={setIsSearchExpanded} params={params} setParams={setParams} />
                <AdvancedSearch
                  sitesAttributes={sitesAttributes}
                  params={params}
                  setParams={setParams}
                />
              </form>
            </>
          )
          : (
            <div className="flex w-full border border-vamos-blue-gray rounded-md bg-vamos-true-white h-12">
              <div className={`relative text-sm text-vamos-gray ${isRoutePage ? 'w-full' : 'min-w-56 max-w-175.5'} ${chips.length > 0 ? 'flex gap-x-1.5 inline-block pl-3 px-2' : 'justify-center items-center'}`}>
                {chips.length > 0 && (
                <div className="flex items-center w-full overflow-x-hidden">
                  <SearchChipsCarousel chips={chips} onClickCloseChip={onClickCloseChip} isAdvancedSearch />
                </div>
                )}
                <button
                  type="button"
                  aria-label="expand search"
                  data-testid="expand-search-button"
                  className={`${chips.length > 0 ? '' : 'flex h-full items-center justify-between w-full justify-center gap-x-1.5 px-3 py-2.5 text-sm text-vamos-gray'}`}
                  onClick={() => setIsSearchExpanded(!isSearchExpanded)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); setIsSearchExpanded(!isSearchExpanded); } }}
                >
                  <p className="whitespace-nowrap truncate">
                    {chips.length === 0 && searchTitle}
                  </p>
                  <Icon name="search" />
                </button>

              </div>
              {isRoutePage && showZoomAndOffSitesButtons}
            </div>
          )
      )}
    </>
  );
};
AdvancedSearchWrapper.propTypes = {
  setIsSearchExpanded: PropTypes.func.isRequired,
  sitesAttributes: PropTypes.object,
  searchTitle: PropTypes.string,
  params: PropTypes.object.isRequired,
  setParams: PropTypes.func.isRequired,
  search: PropTypes.func.isRequired,
  searchParams: PropTypes.object.isRequired,
  setSearchParams: PropTypes.func.isRequired,
  updateSearchParamsAndUrl: PropTypes.func.isRequired,
  isSearchExpanded: PropTypes.bool.isRequired,
  isRoutePage: PropTypes.bool,
  hideOffRouteSites: PropTypes.func,
  zoomToRoute: PropTypes.func,
  isHideMarkers: PropTypes.bool,
};

export default AdvancedSearchWrapper;
